import React from 'react';
import { Typography, Space, Button, Tag, Badge, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { 
  InfoCircleOutlined, 
  CalendarOutlined,
  UserOutlined,
  RocketOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  ThunderboltOutlined,
  EnvironmentOutlined,
  ApartmentOutlined,
  ToolOutlined,
  BuildOutlined,
  ColumnWidthOutlined,
  BgColorsOutlined,
  LinkOutlined,
  ControlOutlined,
  EyeOutlined,
  ApiOutlined,
  FunctionOutlined,
  SettingOutlined,
  DatabaseOutlined,
  HeatMapOutlined,
  GlobalOutlined,
  DeploymentUnitOutlined,
  CompassOutlined,
  FileTextOutlined,
  ClusterOutlined,
  PartitionOutlined,
  CheckCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  TagsOutlined,
  ProfileOutlined,
  SlidersFilled,
  DashboardOutlined,
  CodeOutlined,
  AppstoreOutlined,
  BranchesOutlined,
  LayoutOutlined,
  RobotOutlined,
  AreaChartOutlined
} from '@ant-design/icons';
import { 
  CodeDisplay, 
  SeverityTag, 
  ChangeTypeTag, 
  getStatusColor
} from './UtilityComponents';
import { 
  BaseChange, 
  MissionChange, 
  OperationalScenarioChange,
  RequirementChange,
  FunctionChange,
  ParameterChange,
  LogicalChange,
  BOMChange,
  CADChange,
  ImpactedItem
} from '../../../types/changeAwareness';
import useColors from '../../../hooks/useColors';
import OpenIcon from '../../../icons/cmdOpen24.svg';
import RequirementsIcon from '../../../icons/Requirements.svg';
import MissionIcon from '../../../icons/typeTarget48.svg';
import FunctionsIcon from '../../../icons/Functions.svg';
import LogicalIcon from '../../../icons/Logical.svg';
import ParametersIcon from '../../../icons/Parameters.svg';
import EBOMIcon from '../../../icons/typeClass48.svg';
import CADIcon from '../../../icons/typePartComponent48.svg';
import OperationalScenariosIcon from '../../../icons/typeBranchRevision48.svg';

const { Text } = Typography;

// Domain hierarchy for determining downstream relationships
const domainHierarchy = {
  mission: ['operationalScenario', 'requirement', 'parameter', 'function', 'logical', 'cad', 'bom'],
  operationalScenario: ['requirement', 'parameter', 'function', 'logical', 'cad', 'bom'],
  requirement: ['parameter', 'function', 'logical', 'cad', 'bom'],
  parameter: ['function', 'logical', 'cad', 'bom'],
  function: ['logical', 'cad', 'bom'],
  logical: ['cad', 'bom'],
  cad: ['bom'],
  bom: []
};

// Helper function to filter impacted items to only include downstream domains
const getDownstreamImpactedItems = (record: any) => {
  const currentDomain = record.domain;
  const downstreamDomains = domainHierarchy[currentDomain] || [];
  
  if (!record.impactedItems || !Array.isArray(record.impactedItems)) {
    return [];
  }
  
  return record.impactedItems.filter(item => 
    downstreamDomains.includes(item.type)
  );
};

// Extend BaseChange interface to include required fields
export interface StandardBaseChange extends BaseChange {
  id: string;
  category: string;
  changeType: "added" | "modified" | "removed" | string;
  severity: string;
  impactedItems?: ImpactedItem[];
}

// Type for category configuration
interface CategoryConfig {
  color: string;
  icon: React.ReactNode;
  name: string;
}

const defaultCategory: CategoryConfig = {
  color: 'default',
  icon: <FileTextOutlined />,
  name: 'Other'
};

// Domain-specific category configurations
export const missionCategories: Record<string, CategoryConfig> = {
  objective: { color: 'blue', icon: <RocketOutlined />, name: 'Objective' },
  constraint: { color: 'red', icon: <RocketOutlined />, name: 'Constraint' },
  timeline: { color: 'orange', icon: <RocketOutlined />, name: 'Timeline' },
  environment: { color: 'green', icon: <RocketOutlined />, name: 'Environment' },
  capability: { color: 'purple', icon: <RocketOutlined />, name: 'Capability' }
};

export const scenarioCategories: Record<string, CategoryConfig> = {
  operational: { color: 'blue', icon: <CompassOutlined />, name: 'Operational' },
  environmental: { color: 'green', icon: <CompassOutlined />, name: 'Environmental' },
  safety: { color: 'red', icon: <CompassOutlined />, name: 'Safety' },
  performance: { color: 'orange', icon: <CompassOutlined />, name: 'Performance' },
  logistics: { color: 'purple', icon: <CompassOutlined />, name: 'Logistics' },
  tactical: { color: 'geekblue', icon: <CompassOutlined />, name: 'Tactical' },
  test: { color: 'cyan', icon: <CompassOutlined />, name: 'Test' },
  maintenance: { color: 'gold', icon: <CompassOutlined />, name: 'Maintenance' }
};

export const requirementCategories: Record<string, CategoryConfig> = {
  system: { color: 'blue', icon: <FileTextOutlined />, name: 'System' },
  functional: { color: 'geekblue', icon: <FileTextOutlined />, name: 'Functional' },
  performance: { color: 'orange', icon: <FileTextOutlined />, name: 'Performance' },
  interface: { color: 'cyan', icon: <FileTextOutlined />, name: 'Interface' },
  physical: { color: 'purple', icon: <FileTextOutlined />, name: 'Physical' },
  verification: { color: 'lime', icon: <FileTextOutlined />, name: 'Verification' },
  environmental: { color: 'green', icon: <FileTextOutlined />, name: 'Environmental' },
  safety: { color: 'red', icon: <FileTextOutlined />, name: 'Safety' },
  reliability: { color: 'gold', icon: <FileTextOutlined />, name: 'Reliability' }
};

export const parameterCategories: Record<string, CategoryConfig> = {
  performance: { color: 'orange', icon: <ControlOutlined />, name: 'Performance' },
  physical: { color: 'purple', icon: <ControlOutlined />, name: 'Physical' },
  electrical: { color: 'blue', icon: <ControlOutlined />, name: 'Electrical' },
  thermal: { color: 'red', icon: <ControlOutlined />, name: 'Thermal' },
  environmental: { color: 'green', icon: <ControlOutlined />, name: 'Environmental' },
  operational: { color: 'cyan', icon: <ControlOutlined />, name: 'Operational' },
  design: { color: 'geekblue', icon: <ControlOutlined />, name: 'Design' },
  safety: { color: 'volcano', icon: <ControlOutlined />, name: 'Safety' },
  reliability: { color: 'gold', icon: <ControlOutlined />, name: 'Reliability' }
};

export const functionCategories: Record<string, CategoryConfig> = {
  control: { color: 'blue', icon: <SettingOutlined />, name: 'Control' },
  sensing: { color: 'cyan', icon: <SettingOutlined />, name: 'Sensing' },
  processing: { color: 'orange', icon: <SettingOutlined />, name: 'Processing' },
  communication: { color: 'purple', icon: <SettingOutlined />, name: 'Communication' },
  propulsion: { color: 'red', icon: <SettingOutlined />, name: 'Propulsion' }
};

export const logicalCategories: Record<string, CategoryConfig> = {
  architecture: { color: 'blue', icon: <ClusterOutlined />, name: 'Architecture' },
  interface: { color: 'cyan', icon: <ClusterOutlined />, name: 'Interface' },
  logic: { color: 'orange', icon: <ClusterOutlined />, name: 'Logic' },
  control: { color: 'purple', icon: <ClusterOutlined />, name: 'Control' },
  dataflow: { color: 'green', icon: <ClusterOutlined />, name: 'Dataflow' }
};

export const cadCategories: Record<string, CategoryConfig> = {
  assembly: { color: 'blue', icon: <BuildOutlined />, name: 'Assembly' },
  part: { color: 'cyan', icon: <BuildOutlined />, name: 'Part' },
  drawing: { color: 'orange', icon: <BuildOutlined />, name: 'Drawing' },
  model: { color: 'purple', icon: <BuildOutlined />, name: 'Model' },
  tolerance: { color: 'green', icon: <BuildOutlined />, name: 'Tolerance' },
  component: { color: 'geekblue', icon: <BuildOutlined />, name: 'Component' },
  material: { color: 'magenta', icon: <BuildOutlined />, name: 'Material' },
  geometry: { color: 'gold', icon: <BuildOutlined />, name: 'Geometry' }
};

export const bomCategories: Record<string, CategoryConfig> = {
  part: { color: 'blue', icon: <PartitionOutlined />, name: 'Part' },
  assembly: { color: 'cyan', icon: <PartitionOutlined />, name: 'Assembly' },
  supplier: { color: 'orange', icon: <PartitionOutlined />, name: 'Supplier' },
  material: { color: 'purple', icon: <PartitionOutlined />, name: 'Material' },
  specification: { color: 'green', icon: <PartitionOutlined />, name: 'Specification' }
};

// Common category renderer function
export const renderCategory = (category: string, domainCategories: Record<string, CategoryConfig>) => {
  if (!category || !domainCategories[category]) {
    return <Tag color="default">Unknown</Tag>;
  }

  const config = domainCategories[category];
  
  return (
    <Tag color={config.color} icon={config.icon}>
      {config.name}
    </Tag>
  );
};

// Domain-specific category renderers
export const renderMissionCategory = (category: string) => {
  const config = missionCategories[category?.toLowerCase()] || defaultCategory;
  return <Tag color={config.color}>{config.icon} {config.name.toUpperCase()}</Tag>;
};

export const renderOperationalScenarioCategory = (category: string) => {
  return <Tag color="green">{category.toUpperCase()}</Tag>;
};

export const renderRequirementCategory = (category: string) => {
  const config = requirementCategories[category?.toLowerCase()] || defaultCategory;
  return <Tag color={config.color}>{config.icon} {config.name.toUpperCase()}</Tag>;
};

export const renderParameterCategory = (category: string) => {
  const config = parameterCategories[category?.toLowerCase()] || defaultCategory;
  return <Tag color={config.color}>{config.icon} {config.name.toUpperCase()}</Tag>;
};

export const renderFunctionCategory = (category: string) => {
  const config = functionCategories[category?.toLowerCase()] || defaultCategory;
  return <Tag color={config.color}>{config.icon} {config.name.toUpperCase()}</Tag>;
};

export const renderLogicalCategory = (category: string) => {
  const config = logicalCategories[category?.toLowerCase()] || defaultCategory;
  return <Tag color={config.color}>{config.icon} {config.name.toUpperCase()}</Tag>;
};

export const renderCADCategory = (category: string) => {
  return <Tag color="blue">{category.toUpperCase()}</Tag>;
};

export const renderBOMCategory = (category: string) => {
  return <Tag color="orange">{category.toUpperCase()}</Tag>;
};

// Helper function to get the appropriate category renderer based on domain
export const getCategoryRendererByDomain = (domain: string) => {
  switch (domain) {
    case 'mission':
      return renderMissionCategory;
    case 'operationalScenario':
      return renderOperationalScenarioCategory;
    case 'requirement':
      return renderRequirementCategory;
    case 'parameter':
      return renderParameterCategory;
    case 'function':
      return renderFunctionCategory;
    case 'logical':
      return renderLogicalCategory;
    case 'cad':
      return renderCADCategory;
    case 'bom':
      return renderBOMCategory;
    default:
      return (category: string) => <Tag color="default">{category}</Tag>;
  }
};

// Combined category renderer that automatically detects the domain based on the ID fields present
export const renderAutoDetectedCategory = (record: any) => {
  if (record.missionId) {
    return renderMissionCategory(record.category);
  } else if (record.scenarioId) {
    return renderOperationalScenarioCategory(record.category);
  } else if (record.requirementId) {
    return renderRequirementCategory(record.category);
  } else if (record.parameterId) {
    return renderParameterCategory(record.category);
  } else if (record.functionId) {
    return renderFunctionCategory(record.category);
  } else if (record.logicalId) {
    return renderLogicalCategory(record.category);
  } else if (record.cadId) {
    return renderCADCategory(record.category);
  } else if (record.bomId) {
    return renderBOMCategory(record.category);
  } else {
    return <Tag color="default">{record.category}</Tag>;
  }
};

// Helper function to detect the domain of a change based on its ID fields
export const detectDomain = (record: any): string => {
  if (record.missionId) return 'mission';
  if (record.scenarioId) return 'operationalScenario';
  if (record.requirementId) return 'requirement';
  if (record.parameterId) return 'parameter';
  if (record.functionId) return 'function';
  if (record.logicalId) return 'logical';
  if (record.cadId) return 'cad';
  if (record.bomId) return 'bom';
  return 'unknown';
};

// Helper function to get the domain-specific ID of a change
export const getDomainSpecificId = (record: any): string | null => {
  if (record.missionId) return record.missionId;
  if (record.scenarioId) return record.scenarioId;
  if (record.requirementId) return record.requirementId;
  if (record.parameterId) return record.parameterId;
  if (record.functionId) return record.functionId;
  if (record.logicalId) return record.logicalId;
  if (record.cadId) return record.cadId;
  if (record.bomId) return record.bomId;
  return null;
};

// Standard column configurations
export const getStandardColumns = <T extends StandardBaseChange>(
  handleViewDetails: (record: T) => void,
  categoryRenderer: (category: string, record: T) => React.ReactNode,
  options?: {
    idField?: string;
    idLabel?: string;
    showImpact?: boolean;
  }
): ColumnsType<T> => {
  const idField = options?.idField || 'id';
  const idLabel = options?.idLabel || 'ID';
  
  const columns: ColumnsType<T> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (text) => <Text>{text}</Text>,
      sorter: (a, b) => a.id.localeCompare(b.id),
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => {
        // Determine which icon to use based on the domain
        let DomainIcon;
        switch (record.domain) {
          case 'mission':
            DomainIcon = MissionIcon;
            break;
          case 'operationalScenario':
            DomainIcon = OperationalScenariosIcon;
            break;
          case 'requirement':
            DomainIcon = RequirementsIcon;
            break;
          case 'parameter':
            DomainIcon = ParametersIcon;
            break;
          case 'function':
            DomainIcon = FunctionsIcon;
            break;
          case 'logical':
            DomainIcon = LogicalIcon;
            break;
          case 'cad':
            DomainIcon = CADIcon;
            break;
          case 'bom':
            DomainIcon = EBOMIcon;
            break;
          default:
            DomainIcon = null;
        }

        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {DomainIcon && <img src={DomainIcon} alt={record.domain} style={{ width: '24px', height: '24px', marginRight: '8px' }} />}
              <Text>{text}</Text>
            </div>
            <a 
              href="#" 
              onClick={(e) => {
                e.stopPropagation();
                window.open('about:blank', '_blank');
              }}
              style={{ marginLeft: '10px' }}
            >
              <img src={OpenIcon} alt="Open" style={{ width: '24px', height: '24px' }} />
            </a>
          </div>
        );
      },
      ellipsis: true,
      width: '40%',
      className: 'title-column'
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category, record) => categoryRenderer(category, record),
      sorter: (a, b) => a.category.localeCompare(b.category),
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Type',
      dataIndex: 'changeType',
      key: 'changeType',
      render: (type) => <ChangeTypeTag type={type} />,
      filters: [
        { text: 'Added', value: 'added' },
        { text: 'Modified', value: 'modified' },
        { text: 'Removed', value: 'removed' }
      ],
      onFilter: (value, record) => record.changeType === value,
      sorter: (a, b) => a.changeType.localeCompare(b.changeType),
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => {
        // Format to show only date of last change
        const formattedDate = new Date(date).toISOString().split('T')[0];
        return (
          <Space>
            <CalendarOutlined />
            <span>{formattedDate}</span>
          </Space>
        );
      },
      sorter: (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      defaultSortOrder: 'descend'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const color = getStatusColor(status);
        return <Badge status={color as any} text={status.charAt(0).toUpperCase() + status.slice(1)} />;
      },
      filters: [
        { text: 'Approved', value: 'approved' },
        { text: 'Pending', value: 'pending' },
        { text: 'Rejected', value: 'rejected' },
        { text: 'Reviewed', value: 'reviewed' }
      ],
      onFilter: (value, record) => record.status === value,
      sorter: (a, b) => a.status.localeCompare(b.status),
      sortDirections: ['ascend', 'descend'],
      width: 120,
      className: 'status-column'
    },
    {
      title: 'Author',
      dataIndex: 'author',
      key: 'author',
      render: (author) => (
        <Space>
          <UserOutlined />
          <span>{author}</span>
        </Space>
      ),
      sorter: (a, b) => a.author.localeCompare(b.author),
      sortDirections: ['ascend', 'descend'],
      width: 200
    }
  ];

  // Add impact column if specified
  if (options?.showImpact) {
    columns.push({
      title: 'Pot. Impact',
      key: 'impact',
      render: (_, record) => {
        const downstreamItems = getDownstreamImpactedItems(record);
        const total = downstreamItems.length;
        
        return (
          <Tooltip title="Downstream items potentially impacted by this change">
            <span>{total} items</span>
          </Tooltip>
        );
      },
      width: 120
    });
  }

  return columns;
};

// Standard column configurations
export const getChangeColumns = (domainType: string, showActions: boolean = true) => {
  // ... existing code ...
};

/**
 * Configuration for the different change categories used across the application
 * This provides consistent styling and icons for each category
 */
export const useCategoryConfigurations = () => {
  const colors = useColors();
  
  return {
    // Mission domain
    mission: {
      key: 'mission',
      name: 'Mission',
      color: colors.category.mission,
      bgColor: `${colors.category.mission}15`,
      borderColor: `${colors.category.mission}40`,
      icon: <RocketOutlined style={{ color: colors.category.mission }} />,
      defaultColumns: ['id', 'title', 'status', 'changedBy', 'changedDate'],
    },
    
    // Scenario domain
    scenario: {
      key: 'scenario',
      name: 'Scenario',
      color: colors.category.mission,
      bgColor: `${colors.category.mission}15`,
      borderColor: `${colors.category.mission}40`,
      icon: <AreaChartOutlined style={{ color: colors.category.mission }} />,
      defaultColumns: ['id', 'title', 'status', 'changedBy', 'changedDate'],
    },
    
    // Requirement domain
    requirement: {
      key: 'requirement',
      name: 'Requirement',
      color: colors.category.requirements,
      bgColor: `${colors.category.requirements}15`,
      borderColor: `${colors.category.requirements}40`,
      icon: <ProfileOutlined style={{ color: colors.category.requirements }} />,
      defaultColumns: ['id', 'title', 'status', 'version', 'severity', 'changedBy', 'changedDate'],
    },
    
    // Parameter domain
    parameter: {
      key: 'parameter',
      name: 'Parameter',
      color: colors.category.parameter,
      bgColor: `${colors.category.parameter}15`,
      borderColor: `${colors.category.parameter}40`,
      icon: <SlidersFilled style={{ color: colors.category.parameter }} />,
      defaultColumns: ['id', 'title', 'value', 'unit', 'changedBy', 'changedDate'],
    },
    
    // Function domain
    function: {
      key: 'function',
      name: 'Function',
      color: colors.category.functions,
      bgColor: `${colors.category.functions}15`,
      borderColor: `${colors.category.functions}40`,
      icon: <TagsOutlined style={{ color: colors.category.functions }} />,
      defaultColumns: ['id', 'title', 'status', 'changedBy', 'changedDate'],
    },
    
    // Logical domain
    logical: {
      key: 'logical',
      name: 'Logical',
      color: colors.chart.series4,
      bgColor: `${colors.chart.series4}15`,
      borderColor: `${colors.chart.series4}40`,
      icon: <BranchesOutlined style={{ color: colors.chart.series4 }} />,
      defaultColumns: ['id', 'title', 'status', 'type', 'changedBy', 'changedDate'],
    },
    
    // CAD domain
    cad: {
      key: 'cad',
      name: 'CAD',
      color: colors.category.cad,
      bgColor: `${colors.category.cad}15`,
      borderColor: `${colors.category.cad}40`,
      icon: <LayoutOutlined style={{ color: colors.category.cad }} />,
      defaultColumns: ['id', 'title', 'version', 'type', 'changedBy', 'changedDate'],
    },
    
    // BOM domain
    bom: {
      key: 'bom',
      name: 'BOM',
      color: colors.category.bom,
      bgColor: `${colors.category.bom}15`,
      borderColor: `${colors.category.bom}40`,
      icon: <DatabaseOutlined style={{ color: colors.category.bom }} />,
      defaultColumns: ['id', 'title', 'version', 'changedBy', 'changedDate'],
    },
    
    // Software domain
    software: {
      key: 'software',
      name: 'Software',
      color: colors.chart.series6,
      bgColor: `${colors.chart.series6}15`,
      borderColor: `${colors.chart.series6}40`,
      icon: <CodeOutlined style={{ color: colors.chart.series6 }} />,
      defaultColumns: ['id', 'title', 'version', 'changedBy', 'changedDate'],
    },
    
    // Simulation domain
    simulation: {
      key: 'simulation',
      name: 'Simulation',
      color: colors.chart.series7,
      bgColor: `${colors.chart.series7}15`,
      borderColor: `${colors.chart.series7}40`,
      icon: <DashboardOutlined style={{ color: colors.chart.series7 }} />,
      defaultColumns: ['id', 'title', 'version', 'changedBy', 'changedDate'],
    },
    
    // Manufacturing domain
    manufacturing: {
      key: 'manufacturing',
      name: 'Manufacturing',
      color: colors.chart.series8,
      bgColor: `${colors.chart.series8}15`,
      borderColor: `${colors.chart.series8}40`,
      icon: <BuildOutlined style={{ color: colors.chart.series8 }} />,
      defaultColumns: ['id', 'title', 'status', 'changedBy', 'changedDate'],
    },
    
    // Automation domain
    automation: {
      key: 'automation',
      name: 'Automation',
      color: colors.chart.series3,
      bgColor: `${colors.chart.series3}15`,
      borderColor: `${colors.chart.series3}40`,
      icon: <RobotOutlined style={{ color: colors.chart.series3 }} />,
      defaultColumns: ['id', 'title', 'status', 'changedBy', 'changedDate'],
    },
  };
};

export default useCategoryConfigurations; 